import { Map } from 'immutable';
import localForage from 'localforage';
import every from 'lodash/every';
import isObject from 'lodash/isObject';
import { applyMiddleware, createStore } from 'redux';
import { batchedSubscribe } from 'redux-batched-subscribe';
import { combineEpics, createEpicMiddleware } from 'redux-observable';
import { autoRehydrate, persistStore } from 'redux-persist';
import createMigration from 'redux-persist-migrate';
import immutableTransform from 'redux-persist-transform-immutable';
import { BehaviorSubject, mergeMap } from 'rxjs';

import * as Constants from '../constants';
import { PERSISTOR_KEY } from '../constants/App';
import * as DebugSettingsUtil from '../lib/configuration/DebugSettingsUtil';
import getMiddleware from '../middleware';
import { EntityFilter } from '../reducers/entities';
import { buildRootReducer } from '../reducers/Index';
import { LoginFilter } from '../reducers/Login';
import { UIFilter } from '../reducers/UI';
import rootSaga from '../sagas';
import { backgroundSafeRequestAnimationFrame } from '../tools/Globals';
import tempImmutableTransform from '../tools/ImmutableMigrationTransform';
import Logger from '../tools/Logger';
import createEncryptor from '../tools/ReduxEncryptFilter';

import manifest from './Migrations';
import { setPersistor, setStore } from './StoreAccessor';
import { createPersistConfig } from './StoreAsync';
import composeEnhancers from './utils/composeEnhancers';

const log = Logger.child({ component: 'utils/StoreUtils' });

let _store = null;
let _persistor = null;
let restoredStateFromPersist = false;

/**
 *
 * @param {object} store -
 * @param {object} reduxRouterMiddleware -
 * @param {object} hotModule -
 */
function setupDevtoolsReplays(store, reduxRouterMiddleware, hotModule) {
  // Enable Webpack hot module replacement for reducers
  hotModule.accept('../reducers/Index', () => {
    const nextReducer = require('../reducers/Index');
    store.replaceReducer(nextReducer);
  });
}

function setRestoredStateFromPersist(restoredState) {
  // restored state always at least has the `storage` key, that's why we check > 1
  restoredStateFromPersist = isObject(restoredState) && Object.keys(restoredState).length > 1;
}

function validateStore() {
  if (restoredStateFromPersist && _store) {
    const isStoreValid = every(Constants.Storage.REDUCERS_TO_VALIDATE_AS_MAPS, key => {
      return Map.isMap(_store[key]);
    });
    if (!isStoreValid) {
      return false;
    }
  }
  return true;
}

/**
 * creates and configures redux store
 *
 * @returns {Promise} Promise that resolves to the newly created & configured store
 */
export function createReduxStore() {
  return new Promise((resolve, reject) => {
    if (_store) {
      reject(new Error('store already configured'));
    }

    const { middleware, reduxRouterMiddleware, sagaMiddleware } = getMiddleware();
    const epicMiddleware = createEpicMiddleware();

    // create redux store
    _store = createStore(
      buildRootReducer(),
      undefined,
      composeEnhancers(
        createMigration(manifest, 'storage'),
        autoRehydrate(),
        applyMiddleware(...middleware, epicMiddleware),
        batchedSubscribe(backgroundSafeRequestAnimationFrame),
      ),
    );

    // Set the store singleton for the rest of the app to access
    setStore(_store);

    const epic$ = new BehaviorSubject(combineEpics());

    const rootEpic = (action$, state$) => {
      return epic$.pipe(mergeMap(epic => epic(action$, state$, [])));
    };

    epicMiddleware.run(rootEpic);

    // extensions
    _store.asyncReducers = {};

    _store.runSaga = sagaMiddleware.run;

    if (module.hot) {
      setupDevtoolsReplays(_store, reduxRouterMiddleware, module.hot);
    }

    // App persist configuration
    let transforms = [
      EntityFilter,
      UIFilter,
      LoginFilter,
      immutableTransform({}),
      tempImmutableTransform(),
    ];

    if (DebugSettingsUtil.isStorageObscured()) {
      // TODO: add ReduxCompressFilter if performance allows
      // const compressor = createCompressor();
      const encryptor = createEncryptor({
        secretKey: PERSISTOR_KEY,
        whitelist: Constants.Storage.ENCRYPTED_REDUCERS_ALLOWLIST,
      });
      transforms = transforms.concat([encryptor]);
    }

    const appPersistConfig = createPersistConfig({
      whitelist: Constants.Storage.REDUCERS_ALLOWLIST,
      transforms,
    });

    log.trace('createReduxStore() - starting app persistence');
    // restored persisted state and configure future persistence
    _persistor = persistStore(_store, appPersistConfig, (err, restoredState) => {
      setRestoredStateFromPersist(restoredState);
      log.trace('createReduxStore() - finishing app persistence');

      log.trace('createReduxStore() - returning app store');
      resolve({ store: _store, rootSaga, epic$ });
    });
    // make persistor available on window for tests
    window.persistor = _persistor;
    setPersistor(_persistor);
  });
}

export function getStore() {
  if (!_store) {
    throw new Error('store is not configured.');
  }
  return _store;
}

export function getPersistor() {
  return _persistor;
}

export function tryToRecoverFromStoreError() {
  const persistor = getPersistor();
  const isStoreValid = validateStore();
  // Should only try to purge storage and refresh if we actually
  // rehydrated from storage
  if (persistor && location && restoredStateFromPersist && !isStoreValid) {
    persistor.purge();
    location.reload();
  }
}

function deleteAllCookies() {
  const cookies = document.cookie.split(';');
  cookies.forEach(cookie => {
    const name = cookie.split('=')[0];
    document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
  });
}

export function clearLocalData() {
  return localForage.clear().then(() => {
    deleteAllCookies();
    sessionStorage.clear();
    localStorage.clear();
    return Promise.resolve();
  });
}

export function getReducersToPurge() {
  const allowlistedReducers = Constants.Storage.TOTAL_ALLOWLISTED_REDUCERS;
  return allowlistedReducers.filter(key => {
    return Constants.Storage.REDUCERS_SESSION_INDEPENDENT_ALLOWLIST.indexOf(key) === -1;
  });
}

export function dispatch(action) {
  getStore()?.dispatch(action);
}
